/*
 * /oauth2/authorize/:sessionId — согласие на вход ИИ-агента (MCP-клиента) в портал. Бэкенд auth
 * (CIMD/DCR, app-config auth.*) отправляет сюда пользователя из OAuth-авторизации клиента; после
 * «Разрешить» клиент получает токен с правами этого пользователя (тот же вход GitLab, та же политика прав).
 * Своя страница: штатная @backstage/plugin-auth — только для новой системы фронтенда.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import useAsync from 'react-use/esm/useAsync';
import { Box, Button, Typography, makeStyles } from '@material-ui/core';
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
  ResponseErrorPanel,
  WarningPanel,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

type Session = { id: string; clientId: string; clientName?: string; scope?: string; redirectUri: string };

const useStyles = makeStyles(theme => ({
  row: { display: 'grid', gridTemplateColumns: '12rem 1fr', gap: theme.spacing(1, 2), marginBottom: theme.spacing(2) },
  label: { color: theme.palette.text.secondary },
  value: { wordBreak: 'break-all' },
  actions: { display: 'flex', gap: theme.spacing(2), marginTop: theme.spacing(3) },
}));

const hostOf = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

export const OAuthConsentPage = () => {
  const classes = useStyles();
  const { sessionId = '' } = useParams();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error>();

  const state = useAsync(async () => {
    const base = await discoveryApi.getBaseUrl('auth');
    const res = await fetchApi.fetch(`${base}/v1/sessions/${encodeURIComponent(sessionId)}`);
    if (!res.ok) throw new Error(`Запрос на вход не найден или устарел (HTTP ${res.status}) — начните подключение агента заново`);
    const session = (await res.json()) as Session;
    const identity = await identityApi.getBackstageIdentity();
    return { session, user: identity.userEntityRef, groups: identity.ownershipEntityRefs };
  }, [sessionId]);

  const decide = async (decision: 'approve' | 'reject') => {
    setBusy(true);
    try {
      const base = await discoveryApi.getBaseUrl('auth');
      const res = await fetchApi.fetch(`${base}/v1/sessions/${encodeURIComponent(sessionId)}/${decision}`, { method: 'POST' });
      if (!res.ok) throw new Error(`Не удалось ${decision === 'approve' ? 'разрешить' : 'отклонить'} вход (HTTP ${res.status})`);
      const { redirectUrl } = (await res.json()) as { redirectUrl: string };
      window.location.href = redirectUrl;
    } catch (e: any) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Page themeId="tool">
      <Header title="Вход ИИ-агента" subtitle="Подключение MCP-клиента к порталу" />
      <Content>
        {state.loading && <Progress />}
        {state.error && <ResponseErrorPanel error={state.error} />}
        {error && <ResponseErrorPanel error={error} />}
        {state.value && (
          <InfoCard title={`«${state.value.session.clientName || hostOf(state.value.session.clientId)}» запрашивает доступ`}>
            <WarningPanel severity="info" title="Агент будет действовать от вашего имени">
              Он получит ваши права в портале: каталог, шаблоны и всё, что разрешено вашим группам. Разрешайте
              только агентам, которые вы запустили сами.
            </WarningPanel>
            <Box mt={2} className={classes.row}>
              <Typography className={classes.label}>Пользователь</Typography>
              <Typography className={classes.value}>{state.value.user}</Typography>
              <Typography className={classes.label}>Клиент</Typography>
              <Typography className={classes.value}>{state.value.session.clientId}</Typography>
              <Typography className={classes.label}>Вернёт токен на</Typography>
              <Typography className={classes.value}>{state.value.session.redirectUri}</Typography>
              <Typography className={classes.label}>Доступ</Typography>
              <Typography className={classes.value}>{state.value.session.scope || 'openid'}</Typography>
            </Box>
            <div className={classes.actions}>
              <Button variant="contained" color="primary" disabled={busy} onClick={() => decide('approve')}>
                Разрешить
              </Button>
              <Button variant="outlined" disabled={busy} onClick={() => decide('reject')}>
                Отклонить
              </Button>
            </div>
          </InfoCard>
        )}
      </Content>
    </Page>
  );
};
